import type { QuestionVisibilityState } from "@/components/survey/Question";
import type { DisqualificationReason } from "@/constants";

import { calculateBMIImperial } from "../bmi";
import { isCareFirstPartner, isHighmarkPartner } from "../partners";

/**
 * This function returns whether a user qualifies for Noom Weight based on
 * the current visibility state.
 *
 * There are typically no restrictions on who can qualify for Noom Weight.
 * However, there are partner specific restrictions:
 *
 * - CareFirst and Highmark users must have a BMI >= 25
 *
 * This is used to determine question visibility so it does mean it will return
 * null if some questions are not answered
 *
 * @param {QuestionVisibilityState} state The entire visibility state
 * @returns {DisqualificationReason | null} Returns a disqualification reason
 * if the user does not qualify for Noom Weight, null if the user qualifies
 */
export const qualifiesForNoomWeight = ({
  partnerInfo,
  surveyAnswers,
}: QuestionVisibilityState): DisqualificationReason | null => {
  const {
    heightWeight: { heightFeetPortion, heightInchesPortion, weight } = {},
  } = surveyAnswers;

  /**
   * If the user hasn't answered the height and weight question, they aren't in
   * a position to be excluded so we return null.
   */
  if (
    heightFeetPortion === undefined ||
    heightInchesPortion === undefined ||
    weight === undefined
  ) {
    return null;
  }

  const bmi = calculateBMIImperial(
    heightFeetPortion,
    heightInchesPortion,
    weight,
  );

  if (isCareFirstPartner(partnerInfo) || isHighmarkPartner(partnerInfo)) {
    if (bmi < 25) {
      return "BMI_TOO_LOW";
    }
  }

  return null;
};
